Ext.define('SA.data.View', {
    alias: 'view.view',

    mixins: {
        observable: 'Ext.util.Observable'
    },

    statics: {
        create({type, project, params}) {
            var cls = SA.data.View.getClass(type),
                locations = params.locations || project.getLocations(),
                attributes = params.attributes || project.getAttributes(),
                censusRelease = params.censusRelease || project.getCensusRelease(),
                name = project.getDefaultViewName(type),
                toLocationItem = id => SA.data.LocationsCache.toLocationItem(id),
                attributeMatchesCensusRelease = id => SA.data.AttributesCache.hasCensusRelease(id, censusRelease),
                locationMatchesCensusRelease = id => SA.data.LocationsCache.locationAvailableInCensusRelease(id, censusRelease);

            return cls.create({
                project,
                censusRelease,
                name,
                data: {type, buildingBlocksCensusRelease: censusRelease, name, filterVisibility: 'strikeout'},
                country: SA.Country.get(project.getCountry()),
                locationItems: locations.filter(locationMatchesCensusRelease).map(toLocationItem),
                attributes: attributes.filter(attributeMatchesCensusRelease),
                businessSearch: project.getBusinessSearches()[0] || null,
                ...Reflect.has(params, 'geographicUnit') ? {geographicUnit: params.geographicUnit} : {}
            });
        },

        deserialize({project, data}) {
            var id = data.id,
                cls = this.getClass(data.type);

            Reflect.deleteProperty(data, 'id');

            data.buildingBlocksCensusRelease = Number(data.buildingBlocksCensusRelease) || project.getCensusRelease();

            if (Ext.isFunction(cls.instantiate)) {
                return cls.instantiate({id, project, data, saved: Ext.clone(data)});
            }

            return new cls({id, project, data, saved: Ext.clone(data)});
        },

        getClass(type) {
            var type = {table: 'comparison', childTable: 'ranking'}[type] || type;

            return Ext.ClassManager.getByAlias('view.' + type);
        }
    },

    constructor({id = null, project, data, saved = {}}) {
        this.id = id;
        this.project = project;
        this.data = data;
        this.saved = saved;

        this.uiId = Ext.id();

        this.mixins.observable.constructor.call(this);

        this.on('change:location', this.onChangeBuildingBlock, this);
        this.on('change:attribute', this.onChangeBuildingBlock, this);
        this.on('change:businessSearch', this.onChangeBuildingBlock, this);
    },

    capture() {
        return Object.assign({}, this.data);
    },

    commit(data) {
        this.saved = data;
    },

    equalFieldValues(valueA, valueB) {
        return SA.Json.equal(valueA, valueB);
    },

    fireEventArgs(name, args) {
        var project = this.project;

        this.mixins.observable.fireEventArgs.call(this, name, args);

        if (project && name.slice(0, 7) === 'change:') {
            project.fireEventArgs('change:view:' + name.slice(7), args);
        }
    },

    getCategories() {
        var categories = SA.data.DataCategories.get({country: this.getCountry()}),
            hasSupportedImports = this.getSupportedDatasetModels().some(dataset => dataset.isImport()),
            viewTypeFilter = {
                acsTable: category => category.hasAcsData,
                scarboroughCrosstab: category => category.hasScarboroughCrosstabData,
                simmonsCrosstab: category => category.hasSimmonsCrosstabData
            }[this.getType()];

        if (viewTypeFilter) {
            return categories.filter(viewTypeFilter);
        }
        else if (!hasSupportedImports) {
            return categories.filter(category => !SA.data.CountryCategoryList.isImportsCategoryId(category.id));
        }

        return categories;
    },

    getCensusRelease() {
        return this.getCensusReleaseYear();
    },

    getCensusReleaseGeoUnits() {
        return this.project.getCensusReleaseGeoUnits(this.getCensusReleaseYear());
    },

    getCensusReleaseId() {
        return new SA.data.CensusReleaseId({countryCode: this.getCountry(), censusReleaseYear: this.getCensusReleaseYear()});
    },

    getCensusReleaseRange() {
        return this.getCountryModel().getCensusReleaseRange(this.getCensusReleaseYear());
    },

    getCensusReleaseRangeEnd() {
        return this.getCensusReleaseRange()[1];
    },

    getCensusReleaseYear() {
        return this.data.buildingBlocksCensusRelease;
    },

    getController() {
        this.controller = this.controller || this.createController();

        return this.controller;
    },

    getCountry() {
        return this.getProject().getCountry();
    },

    getCountryModel() {
        return SA.model.Country.get(this.getCountry());
    },

    getDataFoldersFilterLocation() {
        return null;
    },

    getDataQuery() {
        var where = SA.data.AttributesQuery.getCountryAndStatusVisiblePredicate(this.getCountry(), this.getCensusRelease());

        where.push(['in', 'attribute_class', SA.data.AttributeClasses.getPolygonClasses()]);

        if (this.hasLatestCensusRelease()) {
            where.push(['not', ['=', 'h_historical', 'true']]);
        }

        return where;
    },

    getLatestEditionOption() {
        return SA.DataSearchEditionOption.latest();
    },

    getEditionOptionForYear(year) {
        return SA.DataSearchEditionOption.forYear(year);
    },

    getDefaultEditionOption() {
        if (this.isCrosstab()) {
            return this.hasLatestYear() ? this.getLatestEditionOption() : this.getEditionOptionForYear(this.getYear());
        }
        else if (this.getCountryModel().isCanada() && this.getCensusReleaseYear() === 2011) {
            return this.getEditionOptionForYear(2017); 
        }

        return this.getLatestEditionOption();
    },

    getDiscoverableGeoUnits() {
        return this.getProject().getDiscoverableGeographicUnits();
    },

    getEditionOptions() {
        if (this.isCrosstab()) {
            return [this.getDefaultEditionOption()];
        }

        var editionsForCensusRelease = this.getCountryModel().getEditions(this),
            yearsWithData = SA.data.Years.forCountry(this.getCountry()),
            nonLatestEditionOptions = Ext.Array.intersect(yearsWithData, editionsForCensusRelease)
                .sort()
                .reverse()
                .map(year => this.getEditionOptionForYear(year));

        return [this.getLatestEditionOption(), ...nonLatestEditionOptions];
    },

    getProject() {
        return this.project;
    },

    getId() {
        return this.id;
    },

    getIdPromise() {
        return new Promise(resolve => {
            if (this.id) {
                resolve(this.id);
            }
            else {
                this.on({save: () => resolve(this.id), single: true});
            }
        });
    },

    getName() {
        return this.data.name;
    },

    getProjectAttributes() {
        return this.getProject().getAttributes();
    },

    getProjectLocations() {
        return this.getProject().getLocations();
    },

    getReferencedAttributes() {
        return this.getAttributes?.() ?? [];
    },

    getSupportedDatasetModels() {
        return SA.data.Vendors.getDatasetModels()
            .filter(dataset => dataset.hasCountryCode(this.getCountry()))
            .filter(dataset => dataset.hasCensusReleaseYear(this.getCensusReleaseYear()))
            .filter(dataset => this.supportsDatasetSeries(dataset.getId()));
    },

    getSupportedLocationGeoUnitIds() {
        return SA.data.GeographicUnits.findIds({
            country: this.getCountry(),
            includeCountry: true,
            units: this.getProject().getDiscoverableGeographicUnits(),
            censusRelease: this.getCensusRelease()
        });
    },

    getSupportedProjectAttributes() {
        return this.getProject().getAttributes().filter(this.supportsAttribute, this);
    },

    getSupportedProjectLocations() {
        return this.getProject().getLocations().filter(this.supportsLocation, this);
    },

    getType() {
        return this.data.type;
    },

    getUiId() {
        return this.uiId;
    },

    getUpdates(capture) {
        var saved = this.saved,
            id = this.getId(),
            updates = {};

        if (id) {
            updates.id = id;
        }

        for (let field in capture) {
            if (!this.equalFieldValues(capture[field], saved[field])) {
                updates[field] = capture[field];
            }
        }

        return updates;
    },

    getViews() {
        return [];
    },

    hasController() {
        return !!this.controller;
    },

    hasHistoricalCensusRelease() {
        return !this.hasLatestCensusRelease();
    },

    hasLatestCensusRelease() {
        var country = SA.model.Country.get(this.getCountry());

        return this.getCensusRelease() === country.getLatestCensusRelease();
    },

    isChanged() {
        var data = this.data,
            saved = this.saved;

        for (let field in data) {
            if (!this.equalFieldValues(data[field], saved[field])) {
                return true;
            }
        }

        return false;
    },

    isAcsTable() {
        return SA.view.ViewTypes.isAcsTable(this.getType());
    },

    isCrosstab() {
        return SA.view.ViewTypes.isCrosstab(this.getType());
    },



        isViewable() {
        return this.isSavable();
    },

    onChangeBuildingBlock() {
        SA.data.Projects.fireChangedViewBuildingBlock(this);
    },

    onRemove() {
        this.fireEvent('remove');
        Ext.destroyMembers(this, 'controller');
    },

    projectHasLocation(location) {
        return this.getProject().getLocations().includes(location);
    },

    save() {
        this.project.save();
    },

    set(property, value) {
        var data = this.data;

        if (data[property] !== value) {
            data[property] = value;

            return true;
        }

        return false;
    },

    setId(id) {
        this.id = id;

        if (id == null) {
            this.saved = {};
        }
    },

    setName(name) {
        if (name.trim() === '') {
            return;
        }

        this.setWithEvent('name', name);
    },

    setWithEvent(property, value) {
        if (this.set(property, value)) {
            this.fireEvent('change:' + property, this);

            return true;
        }

        return false;
    },

    supportsAttribute(attribute) {
        var matchesCensusRelease = SA.data.AttributesCache.hasCensusRelease(attribute, this.getCensusRelease());

        return matchesCensusRelease && this.self.supportsAttribute(attribute);
    },

    supportsDatasetSeries(series) {
        return SA.data.Vendors.getDatasetModel(series).isPolygon();
    },

    supportsLocation(location) {
        return SA.data.LocationsCache.locationAvailableInCensusRelease(location, this.getCensusRelease());
    }
});